/**
 * A football result as the score screens enter and show it (football v2, behind the registry's
 * football_v2). The server's App\Scoring\FootballResult is the authority: what is checked here is
 * what it would refuse, so the organiser hears it before a round trip.
 *
 *   full time          always
 *   after extra time   cumulative; offered only when a knockout is level at full time
 *   penalties          offered only while a knockout is still level
 *
 * What is sent: `sets` is the full-time score, `score_meta` carries full_time, extra_time and
 * penalties. The server derives the score in play and the rest.
 */
import {Pair, Payload, Side} from './payloads';
import {ScoringProfile} from './profile';

export interface TwoBoxes {
    a: unknown;
    b: unknown;
}

export interface FootballState {
    fullTime: TwoBoxes;
    extraTime: TwoBoxes;
    penalties: TwoBoxes;
}

/** How a game ended when it was not played out. */
export type FootballResultKind = 'walkover' | 'awarded' | 'abandoned' | 'postponed';

export const FOOTBALL_RESULTS: Array<[FootballResultKind, string]> = [
    ['walkover', 'Walkover'], ['awarded', 'Awarded'], ['abandoned', 'Abandoned'], ['postponed', 'Postponed'],
];

/** A second row after a level full time is extra time when it adds at most this many goals, else penalties. */
const EXTRA_TIME_GOALS = 2;

type GameLike = {sets?: any; score_meta?: any} | null | undefined;

const EMPTY: TwoBoxes = {a: '', b: ''};
const blank = (v: unknown): boolean => v === '' || v === null || v === undefined;
const num = (v: unknown): number | null => (blank(v) || Number.isNaN(Number(v)) ? null : Math.trunc(Number(v)));
const boxes = (pair: unknown): TwoBoxes => (Array.isArray(pair) && pair.length >= 2 ? {a: pair[0], b: pair[1]} : {...EMPTY});
const empty = (row: TwoBoxes): boolean => blank(row.a) && blank(row.b);
const pairOf = (row: TwoBoxes): [number, number] | null => {
    const a = num(row.a), b = num(row.b);
    return a === null || b === null ? null : [a, b];
};
const asPair = (value: unknown): [number, number] | null => (Array.isArray(value) ? pairOf(boxes(value)) : null);

export function isFootballV2(profile: ScoringProfile | null | undefined): boolean {
    return !!profile?.footballV2;
}

/** The deciders the event settles a level knockout game by. */
export function footballDeciders(profile: ScoringProfile): {extraTime: boolean; penalties: boolean} {
    const decider = String(profile.defaults?.knockout_decider || 'extra_time_penalties');
    return {extraTime: decider.includes('extra_time'), penalties: decider.includes('penalties')};
}

/** What the screen offers beyond the full-time score, as the score stands. */
export function footballOffers(profile: ScoringProfile, state: FootballState, knockout: boolean): {extraTime: boolean; penalties: boolean} {
    const fullTime = pairOf(state.fullTime);
    if (!knockout || !fullTime || fullTime[0] !== fullTime[1]) return {extraTime: false, penalties: false};
    const allowed = footballDeciders(profile);
    const extraTime = allowed.extraTime ? pairOf(state.extraTime) : null;
    const stillLevel = !extraTime || extraTime[0] === extraTime[1];
    return {extraTime: allowed.extraTime, penalties: allowed.penalties && stillLevel};
}

export function footballPayload(profile: ScoringProfile, state: FootballState, knockout: boolean): Payload {
    const fullTime = pairOf(state.fullTime);
    const sets: Pair[] = [[num(state.fullTime.a), num(state.fullTime.b)]];
    if (!fullTime) return {sets, score_meta: null, errors: ['Enter the full-time score for both sides.']};
    if (fullTime[0] < 0 || fullTime[1] < 0) return {sets, score_meta: null, errors: ['A score cannot be negative.']};

    const meta: Record<string, any> = {full_time: fullTime};
    const offers = footballOffers(profile, state, knockout);
    const allowed = footballDeciders(profile);
    const errors: string[] = [];
    const done = (): Payload => ({sets, score_meta: meta, errors});
    if (!offers.extraTime && !offers.penalties) return done();

    if (offers.extraTime && !empty(state.extraTime)) {
        const extraTime = pairOf(state.extraTime);
        if (!extraTime) { errors.push('Enter the score after extra time for both sides.'); return done(); }
        if (extraTime[0] < fullTime[0] || extraTime[1] < fullTime[1]) {
            errors.push(`The score after extra time includes full time: it cannot be lower than ${fullTime[0]}-${fullTime[1]}.`);
            return done();
        }
        meta.extra_time = extraTime;
        if (extraTime[0] !== extraTime[1]) return done();
    }

    if (offers.penalties && !empty(state.penalties)) {
        const penalties = pairOf(state.penalties);
        if (!penalties) { errors.push('Enter the penalties for both sides.'); return done(); }
        if (penalties[0] === penalties[1]) { errors.push('Penalties must have a winner.'); return done(); }
        meta.penalties = penalties;
        return done();
    }

    // Still level, and nothing has settled it.
    if (meta.extra_time) {
        errors.push(allowed.penalties
            ? 'Still level after extra time: enter the penalties.'
            : 'Still level after extra time: this event has no penalties, so the game has no winner.');
    } else {
        errors.push(`Level at full time in a knockout: enter ${allowed.extraTime && allowed.penalties ? 'the score after extra time, or the penalties'
            : allowed.extraTime ? 'the score after extra time' : 'the penalties'}.`);
    }
    return done();
}

/**
 * The fields for a game that may already hold a score. `legacyRows` is set when the game holds
 * rows from before the structured result: they are read as full time, extra time and penalties
 * where they could be, and shown to the organiser either way, because saving replaces them.
 */
export function footballInitial(game: GameLike): {state: FootballState; legacyRows: number[][] | null} {
    const meta = game?.score_meta && typeof game.score_meta === 'object' ? game.score_meta : {};
    const rows: number[][] = (Array.isArray(game?.sets) ? game!.sets : [])
        .filter((row: unknown) => Array.isArray(row) && row.length >= 2 && !(blank(row[0]) && blank(row[1])))
        .map((row: any[]) => [Number(row[0]) || 0, Number(row[1]) || 0]);

    if (rows.length > 1) {
        const [first, second, third] = rows;
        const state: FootballState = {fullTime: {...EMPTY}, extraTime: {...EMPTY}, penalties: {...EMPTY}};
        const level = first[0] === first[1];
        const cumulative = second[0] >= first[0] && second[1] >= first[1];
        if (level && rows.length === 3 && cumulative && second[0] === second[1] && third[0] !== third[1]) {
            return {state: {fullTime: boxes(first), extraTime: boxes(second), penalties: boxes(third)}, legacyRows: rows};
        }
        if (level && rows.length === 2) {
            const added = (second[0] - first[0]) + (second[1] - first[1]);
            if (cumulative && added <= EXTRA_TIME_GOALS) return {state: {...state, fullTime: boxes(first), extraTime: boxes(second)}, legacyRows: rows};
            if (second[0] !== second[1]) return {state: {...state, fullTime: boxes(first), penalties: boxes(second)}, legacyRows: rows};
        }
        return {state, legacyRows: rows};
    }

    const fullTime = asPair(meta.full_time) ?? (rows[0] ? [rows[0][0], rows[0][1]] as [number, number] : null);
    return {
        state: {
            fullTime:  fullTime ? boxes(fullTime) : {...EMPTY},
            extraTime: asPair(meta.extra_time) ? boxes(meta.extra_time) : {...EMPTY},
            penalties: asPair(meta.penalties) ? boxes(meta.penalties) : {...EMPTY},
        },
        legacyRows: null,
    };
}

/** How a game ended when it was not played out: what the footer sends. */
export function footballOutcomePayload(kind: FootballResultKind, winner: Side | null, note: string, score: [unknown, unknown] | null): Payload {
    const errors: string[] = [];
    const needsWinner = kind === 'walkover' || kind === 'awarded';
    if (needsWinner && !winner) errors.push(`Pick the side the ${kind === 'awarded' ? 'game is awarded to' : 'walkover goes to'}.`);

    const given = score ? pairOf({a: score[0], b: score[1]}) : null;
    let sets: Pair[] = [];
    if (kind === 'awarded') {
        if (!given) errors.push('Enter the score the game is awarded with, 3-0 say.');
        else if (winner && (given[0] === given[1] || (given[0] > given[1] ? 't1' : 't2') !== winner)) errors.push('The score must go to the side the game is awarded to.');
        if (given) sets = [given];
    } else if (kind === 'abandoned' && given) {
        sets = [given];
    }

    return {
        sets,
        score_meta: {result: kind, ...(needsWinner && winner ? {winner} : {}), ...(note.trim() ? {note: note.trim()} : {})},
        errors,
    };
}

/**
 * A result that has just been sent, as the server will store it: the score in play (after extra
 * time where it was played) and who it goes to. For a screen that keeps showing the game before
 * it has read it back.
 */
export function footballSaved(result: {sets: Pair[] | number[][]; score_meta: Record<string, any> | null}): {sets: number[][]; outcome: 't1' | 't2' | 'draw' | 'none'} {
    const meta = result.score_meta ?? {};
    const sent = (result.sets || []).map((row) => asPair(row)).filter((row): row is [number, number] => row !== null);
    if (typeof meta.result === 'string') {
        return {sets: sent, outcome: meta.winner === 't1' || meta.winner === 't2' ? meta.winner : 'none'};
    }
    const inPlay = asPair(meta.extra_time) ?? asPair(meta.full_time) ?? sent[0] ?? null;
    if (!inPlay) return {sets: [], outcome: 'none'};
    const decidedBy = inPlay[0] !== inPlay[1] ? inPlay : asPair(meta.penalties);

    return {sets: [inPlay], outcome: !decidedBy || decidedBy[0] === decidedBy[1] ? 'draw' : decidedBy[0] > decidedBy[1] ? 't1' : 't2'};
}

/**
 * What a compact two-row score (a bracket cell, a fixture card) shows beside the score in play:
 * the penalties each side scored, or that extra time settled it. Null when neither applies, and
 * for rows from before, which have no score_meta to read.
 */
export function footballDecider(game: GameLike): {kind: 'pens' | 'aet'; a: number; b: number} | null {
    const meta = game?.score_meta && typeof game.score_meta === 'object' ? game.score_meta : {};
    if (typeof meta.result === 'string') return null;
    const penalties = asPair(meta.penalties);
    if (penalties) return {kind: 'pens', a: penalties[0], b: penalties[1]};
    const extraTime = asPair(meta.extra_time);
    if (extraTime && extraTime[0] !== extraTime[1]) return {kind: 'aet', a: extraTime[0], b: extraTime[1]};
    return null;
}

/** One side's box in a compact score: "2", or "2 (4)" when penalties settled it. */
export function compactScore(value: unknown, side: 0 | 1, decider: ReturnType<typeof footballDecider>): string {
    if (value === null || value === undefined || value === '') return '-';
    return decider?.kind === 'pens' ? `${value} (${side === 0 ? decider.a : decider.b})` : String(value);
}

/**
 * The score line: "2-1", "2-1 aet (1-1)", "3-3 aet (1-1), 6-7 pens", "3-0 awarded". Rows from
 * before are written as they are. Side two's view swaps every pair.
 */
export function footballLine(game: GameLike, perspective: 1 | 2 = 1): string {
    const meta = game?.score_meta && typeof game.score_meta === 'object' ? game.score_meta : {};
    const say = (pair: [number, number] | number[]): string => (perspective === 2 ? `${pair[1]}-${pair[0]}` : `${pair[0]}-${pair[1]}`);
    const rows: number[][] = (Array.isArray(game?.sets) ? game!.sets : [])
        .filter((row: unknown) => Array.isArray(row) && row.length >= 2 && !(blank(row[0]) || blank(row[1])))
        .map((row: any[]) => [Number(row[0]) || 0, Number(row[1]) || 0]);

    const result = typeof meta.result === 'string' ? meta.result : null;
    if (result === 'walkover') return 'Walkover';
    if (result === 'postponed') return 'Postponed';
    if (rows.length === 0) return '';
    if (rows.length > 1) return rows.map(say).join(', ');

    let line = say(rows[0]);
    if (result) return `${line} ${result.replace(/_/g, ' ')}`;
    const fullTime = asPair(meta.full_time);
    if (asPair(meta.extra_time)) line += ` aet${fullTime ? ` (${say(fullTime)})` : ''}`;
    const penalties = asPair(meta.penalties);
    if (penalties) line += `, ${say(penalties)} pens`;
    return line;
}
